import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Video, VideoOff, Mic, MicOff, PhoneOff, User } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const ICE = { iceServers: [{ urls: "stun:stun.l.google.com:19302" }] };

export default function LiveCall() {
  const { room } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const localRef = useRef(null);
  const remoteRef = useRef(null);
  const pcRef = useRef(null);
  const [status, setStatus] = useState("Initialising…");
  const [role, setRole] = useState(null);
  const [micOn, setMicOn] = useState(true);
  const [camOn, setCamOn] = useState(true);
  const [stream, setStream] = useState(null);
  const seenIce = useRef(new Set());

  useEffect(() => {
    if (!user || user === false) { nav("/login"); return; }
    (async () => {
      try {
        setStatus("Requesting camera & mic…");
        const s = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
        if (localRef.current) localRef.current.srcObject = s;
        setStream(s);

        setStatus("Joining room…");
        const { data: joinData } = await api.post(`/rtc/join?room=${encodeURIComponent(room)}`);
        setRole(joinData.role);

        const pc = new RTCPeerConnection(ICE);
        pcRef.current = pc;
        s.getTracks().forEach((t) => pc.addTrack(t, s));

        pc.ontrack = (e) => {
          if (remoteRef.current && e.streams[0]) {
            remoteRef.current.srcObject = e.streams[0];
            setStatus("Connected — sacred conversation in progress.");
          }
        };
        pc.onicecandidate = (e) => {
          if (e.candidate) {
            api.post("/rtc/ice", { room, candidate: e.candidate.toJSON(), from_id: user.id })
              .catch((err) => console.warn("ICE candidate send failed:", err));
          }
        };
        pc.oniceconnectionstatechange = () => {
          if (["failed", "disconnected"].includes(pc.iceConnectionState)) setStatus("Connection lost");
        };

        if (joinData.role === "caller") {
          setStatus("Waiting for other side to join…");
          const offer = await pc.createOffer();
          await pc.setLocalDescription(offer);
          await api.post("/rtc/offer", { room, sdp: { type: offer.type, sdp: offer.sdp }, from_id: user.id });
        }

        // Poll signaling every 2s
        const iv = setInterval(async () => {
          try {
            const { data } = await api.get(`/rtc/poll?room=${encodeURIComponent(room)}&from_id=${user.id}`);
            if (joinData.role === "callee" && data.offer && !pc.currentRemoteDescription) {
              setStatus("Answering incoming call…");
              await pc.setRemoteDescription(new RTCSessionDescription(data.offer.sdp));
              const answer = await pc.createAnswer();
              await pc.setLocalDescription(answer);
              await api.post("/rtc/answer", { room, sdp: { type: answer.type, sdp: answer.sdp } });
            }
            if (joinData.role === "caller" && data.answer && !pc.currentRemoteDescription) {
              await pc.setRemoteDescription(new RTCSessionDescription(data.answer.sdp));
            }
            for (const item of data.ice || []) {
              const key = JSON.stringify(item.candidate);
              if (!seenIce.current.has(key)) {
                seenIce.current.add(key);
                try {
                  await pc.addIceCandidate(new RTCIceCandidate(item.candidate));
                } catch (iceErr) {
                  console.warn("addIceCandidate failed:", iceErr);
                }
              }
            }
          } catch (pollErr) {
            console.warn("RTC poll error:", pollErr);
          }
        }, 2000);

        return () => { clearInterval(iv); pc.close(); s.getTracks().forEach(t => t.stop()); };
      } catch (e) {
        console.error(e);
        setStatus("Camera/mic denied or unavailable.");
        toast.error("Please allow camera & mic");
      }
    })();
    // eslint-disable-next-line
  }, []);

  const toggleMic = () => {
    if (!stream) return;
    stream.getAudioTracks().forEach(t => t.enabled = !micOn);
    setMicOn(!micOn);
  };
  const toggleCam = () => {
    if (!stream) return;
    stream.getVideoTracks().forEach(t => t.enabled = !camOn);
    setCamOn(!camOn);
  };
  const hangup = () => {
    if (pcRef.current) pcRef.current.close();
    if (stream) stream.getTracks().forEach(t => t.stop());
    nav(-1);
  };

  return (
    <div className="min-h-[calc(100vh-80px)] bg-black flex flex-col">
      <div className="p-4 flex items-center justify-between border-b border-white/10">
        <div>
          <div className="text-xs uppercase tracking-widest text-[#D4AF37]">Live Consultation</div>
          <div className="text-sm text-[#A0A4AB]">Room: <span className="font-mono text-[#EAE6DF]">{room}</span> {role && <span>· You are the {role}</span>}</div>
        </div>
        <div className="text-xs text-[#A0A4AB]">{status}</div>
      </div>

      <div className="flex-1 grid md:grid-cols-2 gap-2 p-2">
        <div className="relative rounded-xl overflow-hidden bg-[#0A0C10] border border-white/10">
          <video data-testid="local-video" ref={localRef} autoPlay muted playsInline className="w-full h-full object-cover"/>
          <div className="absolute bottom-3 left-3 px-2 py-1 rounded bg-black/60 text-xs">You</div>
          {!camOn && (
            <div className="absolute inset-0 bg-[#0A0C10] flex items-center justify-center">
              <User size={64} className="text-[#A0A4AB]"/>
            </div>
          )}
        </div>
        <div className="relative rounded-xl overflow-hidden bg-[#0A0C10] border border-white/10">
          <video data-testid="remote-video" ref={remoteRef} autoPlay playsInline className="w-full h-full object-cover"/>
          <div className="absolute bottom-3 left-3 px-2 py-1 rounded bg-black/60 text-xs">Guest</div>
        </div>
      </div>

      <div className="p-6 flex items-center justify-center gap-4">
        <button data-testid="toggle-mic" onClick={toggleMic} className={`w-12 h-12 rounded-full flex items-center justify-center transition ${
          micOn ? "bg-white/10 hover:bg-white/20" : "bg-[#FF5A36] hover:bg-red-600"
        }`}>
          {micOn ? <Mic size={20}/> : <MicOff size={20}/>}
        </button>
        <button data-testid="toggle-cam" onClick={toggleCam} className={`w-12 h-12 rounded-full flex items-center justify-center transition ${
          camOn ? "bg-white/10 hover:bg-white/20" : "bg-[#FF5A36] hover:bg-red-600"
        }`}>
          {camOn ? <Video size={20}/> : <VideoOff size={20}/>}
        </button>
        <button data-testid="hangup-btn" onClick={hangup} className="px-6 h-12 rounded-full bg-[#FF5A36] hover:bg-red-600 inline-flex items-center gap-2">
          <PhoneOff size={18}/> End
        </button>
      </div>

      <div className="text-center pb-6 text-xs text-[#6E737D]">
        Share this URL with the other participant: <span className="text-[#D4AF37]">/live-call/{room}</span>
      </div>
    </div>
  );
}
